/**
 * Why a point could not be spent, in a sentence.
 *
 * The same job `saveFailureText` does for a save, and here for the same reason: a greyed out node
 * with no explanation is a screen that has decided not to tell the player what the rules are. One
 * exhaustive switch, so a refusal added to the union without wording is a type error rather than a
 * node that goes quiet and says nothing.
 */

import { perk } from '@/sim/progression/perkRegistry';
import type { ProgressionFailure } from '@/sim/progression/progressionResult';

export function progressionRefusal(failure: ProgressionFailure): string {
  switch (failure.reason) {
    case 'noPerkPoints':
      return 'No perk points. Level up.';
    case 'noStatPoints':
      return 'No stat points. Level up.';
    case 'atMaxRank':
      return 'Already at its last rank.';
    case 'atStatCap':
      return `Already at ${String(failure.cap)}, which is as high as it goes.`;
    case 'levelTooLow':
      return `Needs level ${String(failure.minLevel)}.`;
    case 'missingRequirement':
      return `Needs ${perk(failure.requires).title} at full rank first.`;
    // Neither of these can reach a screen: both are raised while reading a record, and a record
    // that raises one never becomes a profile. Worded anyway, because the switch is exhaustive and
    // a reason with no words is the failure mode this file exists to prevent.
    case 'unknownPerk':
      return `This build has no perk called "${failure.perkId}".`;
    case 'invalidRank':
      return `${perk(failure.perkId).title} cannot be at rank ${String(failure.rank)}.`;
  }
}
